<template>
  <div class="admin-pc-login">
    <header class="admin-pc-login__header">
      <div class="admin-pc-login__header-inner">
        <div class="admin-pc-login__brand">逻拓后管系统</div>
      </div>
    </header>

    <section class="admin-pc-login__banner">
      <div class="admin-pc-login__banner-inner">
        <h1 class="admin-pc-login__banner-title">逻拓后管系统</h1>
        <p class="admin-pc-login__banner-sub">
          在浏览器中使用后台 sys_user 账号登录，左侧菜单、右侧内容，布局与 Web 后台一致。
        </p>
      </div>
    </section>

    <section class="admin-pc-login__body">
      <div class="admin-pc-login__body-inner">
        <div class="admin-pc-login__intro">
          <h2>统一账号体系</h2>
          <p>与 Web 后台共用 sys_user 账号与密码策略，通过 h5_admin_enabled 字段控制 PC 后管登录权限。</p>
          <ul>
            <li>RSA 加密传输密码</li>
            <li>图形验证码校验</li>
            <li>独立 Token，不与 C 端活动用户冲突</li>
          </ul>
        </div>

        <div class="admin-pc-login__card">
          <h3 class="admin-pc-login__card-title">账号登录</h3>
          <el-form ref="loginForm" :model="loginForm" :rules="loginRules" label-width="0" size="small">
            <el-form-item prop="username">
              <el-input v-model="loginForm.username" placeholder="账号" prefix-icon="el-icon-user" />
            </el-form-item>
            <el-form-item prop="password">
              <el-input
                v-model="loginForm.password"
                type="password"
                placeholder="密码"
                prefix-icon="el-icon-lock"
                show-password
                @keyup.enter.native="handleLogin"
              />
            </el-form-item>
            <el-form-item prop="code">
              <div class="admin-pc-login__code-row">
                <el-input
                  v-model="loginForm.code"
                  placeholder="验证码"
                  prefix-icon="el-icon-key"
                  @keyup.enter.native="handleLogin"
                />
                <img v-if="codeUrl" :src="codeUrl" class="admin-pc-login__code-img" alt="验证码" @click="loadCode">
                <div v-else class="admin-pc-login__code-placeholder" @click="loadCode">刷新验证码</div>
              </div>
            </el-form-item>
            <el-button type="primary" size="small" class="admin-pc-login__submit" :loading="loading" @click="handleLogin">
              登 录
            </el-button>
          </el-form>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { encrypt } from '@/utils/rsaEncrypt'
import { getH5AdminToken } from '@/utils/h5-admin-request'
import { h5AdminGetCode, h5AdminLogin, saveH5AdminLoginResult } from '@/api/h5-admin-auth'
import './admin-pc.less'

const ADMIN_PC_CLASS = 'admin-pc-mode'

export default {
  name: 'H5AdminLogin',
  data() {
    return {
      loginForm: {
        username: '',
        password: '',
        code: '',
        uuid: ''
      },
      loginRules: {
        username: [{ required: true, message: '请输入账号', trigger: 'blur' }],
        password: [{ required: true, message: '请输入密码', trigger: 'blur' }],
        code: [{ required: true, message: '请输入验证码', trigger: 'blur' }]
      },
      codeUrl: '',
      loading: false
    }
  },
  created() {
    document.documentElement.classList.add(ADMIN_PC_CLASS)
    document.body.style.overflow = 'auto'
    this.loadCode()
  },
  beforeDestroy() {
    document.documentElement.classList.remove(ADMIN_PC_CLASS)
    document.body.style.overflow = ''
  },
  methods: {
    loadCode() {
      h5AdminGetCode().then(res => {
        const data = res.data || {}
        this.codeUrl = data.img
        this.loginForm.uuid = data.uuid
      }).catch(() => {
        this.codeUrl = ''
      })
    },
    handleLogin() {
      this.$refs.loginForm.validate(valid => {
        if (!valid) return
        this.loading = true
        const payload = {
          username: this.loginForm.username.trim(),
          password: encrypt(this.loginForm.password),
          code: this.loginForm.code,
          uuid: this.loginForm.uuid
        }
        h5AdminLogin(payload)
          .then(res => {
            saveH5AdminLoginResult(res)
            if (!getH5AdminToken()) {
              this.$message.error('登录凭证保存失败，请重试')
              return
            }
            const redirect = this.$route.query.redirect
            const target = redirect && redirect !== '/admin/login'
              ? redirect
              : { name: 'AdminHome' }
            return this.$router.replace(target)
          })
          .catch(() => {
            this.loadCode()
            this.loginForm.code = ''
          })
          .finally(() => {
            this.loading = false
          })
      })
    }
  }
}
</script>

<style scoped lang="less">
.admin-pc-login {
  min-height: 100vh;
  min-width: 1200px;
  background: #fff;
}

.admin-pc-login__header {
  padding: 16px 40px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.admin-pc-login__header-inner {
  max-width: 1200px;
  margin: 0 auto;
}

.admin-pc-login__brand {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.admin-pc-login__banner {
  background: #f5f7fa;
  padding: 48px 40px;
}

.admin-pc-login__banner-inner {
  max-width: 1200px;
  margin: 0 auto;
}

.admin-pc-login__banner-title {
  margin: 0 0 10px;
  font-size: 24px;
  font-weight: 600;
  color: #303133;
}

.admin-pc-login__banner-sub {
  margin: 0;
  font-size: 14px;
  color: #606266;
  line-height: 1.6;
}

.admin-pc-login__body {
  padding: 40px 40px 56px;
}

.admin-pc-login__body-inner {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 80px;
}

.admin-pc-login__intro {
  flex: 1;
  max-width: 560px;
  padding-top: 12px;

  h2 {
    margin: 0 0 12px;
    font-size: 18px;
    color: #303133;
  }

  p {
    margin: 0 0 16px;
    font-size: 13px;
    line-height: 1.8;
    color: #606266;
  }

  ul {
    margin: 0;
    padding-left: 18px;
    color: #909399;
    line-height: 1.9;
    font-size: 13px;
  }
}

.admin-pc-login__card {
  width: 380px;
  flex-shrink: 0;
  padding: 24px 28px 20px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  background: #fff;
}

.admin-pc-login__card-title {
  margin: 0 0 20px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  text-align: center;
}

.admin-pc-login__code-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.admin-pc-login__code-img,
.admin-pc-login__code-placeholder {
  width: 111px;
  height: 36px;
  flex-shrink: 0;
  cursor: pointer;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
}

.admin-pc-login__code-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #909399;
  background: #f5f7fa;
}

.admin-pc-login__submit {
  width: 100%;
  margin-top: 4px;
}
</style>
